import {
  IconEnvelope,
  IconExternalLinkAlt,
  IconFire,
  IconPhone,
  IconPlus,
  IconPlusCircle,
  IconUserCircle,
} from '@maia/icons';

import {
  Button,
  ButtonMenu,
  CardList,
  PageHeader,
  showToast,
  Subnav,
  Tag,
  TextButton,
  Typography,
} from '@maia/ui';
import { createFileRoute, linkOptions, Outlet } from '@tanstack/react-router';
import { getCustomerOptions } from '../../../../features/customer/customer/queryOptions';
import { validateSearch } from '../../../../utils/pagination';
import {
  getClaimOptions,
  getNotesOptions,
} from '../../../../features/claim/claim/queryOptions';
import { ClaimHandlerInformation } from '../../../../features/claim/claim/claim-handler-information';
import { useCreateClaimNote } from '../../../../features/claim/claim/create-note.options';
import { RecentNoteCard } from '../../../../features/shared/note/recent-note-card';
import { NoteSidedrawer } from '../../../../features/shared/note/note-sidedrawer';
import { fileToBase64, RefProvider } from '@maia/utils';
import { queryClient } from '../../../../main';
import { useQuery } from '@tanstack/react-query';
import { useDrawerStore } from './../../../../stores/drawer.store';
import { AddNoteSidedrawer } from './../../../../features/shared/note/add-note-sidedrawer';
import { IAttachmentEntity } from '@maia/shared';
import { ActivitySidedrawer } from './../../../../features/shared/activity/activity-sidedrawer';
import { TaskSidedrawer } from './../../../../features/shared/task/task-sidedrawer';

export const Route = createFileRoute('/_authenticated/claims/$guid/_layout')({
  component: Page,
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: async ({ context: { queryClient }, params: { guid } }) => {
    const claim = await queryClient.ensureQueryData(getClaimOptions(guid));

    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(claim.customer.guid),
    );

    return { claim, customer };
  },
  validateSearch: validateSearch,
});

function Page() {
  const { openDrawer, closeDrawer } = useDrawerStore();
  const data = Route.useLoaderData();
  const { guid } = Route.useParams();

  const { data: notes } = useQuery(
    getNotesOptions(guid, {
      page: 1,
      limit: 3,
      sort: 'created_at,desc',
    }),
  );

  const { mutateAsync: createClaimNote } = useCreateClaimNote(
    data.customer.guid,
    data.claim.guid,
    () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', data.claim.guid],
      });
      showToast({
        intent: 'success',
        title: 'Notitie toegevoegd',
        config: { position: 'top-right' },
      });
      closeDrawer();
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden niet notitie voor je aanmaken. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  return (
    <div className="layout-sidebar">
      <PageHeader
        className="area-page-header"
        icon={IconFire}
        title="Claim"
        reference={
          data.claim?.reference ? `#${data.claim?.reference}` : undefined
        }
        specs={[
          ...(data.claim.customer.consumer.fullName
            ? [
                {
                  icon: IconUserCircle,
                  text: data.claim.customer.consumer.fullName,
                },
              ]
            : []),

          ...(data.claim.customer.consumer.email
            ? [
                {
                  icon: IconEnvelope,
                  text: data.claim.customer.consumer.email,
                },
              ]
            : []),

          ...(data.claim.customer.consumer.phone
            ? [
                {
                  icon: IconPhone,
                  text: data.claim.customer.consumer.phone,
                },
              ]
            : []),
        ]}
        status={
          <Tag
            size="large"
            label={data.claim.status.label}
            variant={data.claim.status.variant}
          />
        }
        actions={
          <>
            {data.claim.isEmsProduct && (
              <a
                href="https://maia.emsclaimsengine.com/index.php?view=user.login"
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  intent="tertiary"
                  size="default"
                  iconRight={IconExternalLinkAlt}
                >
                  Open in EMS
                </Button>
              </a>
            )}

            <ButtonMenu
              intent="cta"
              size="default"
              iconLeft={IconPlus}
              menuItems={[
                {
                  icon: 'check-square',
                  text: 'Nieuwe taak',
                  href: `/tasks/new/claim?guid=${data.claim.guid}`,
                },
                {
                  icon: 'notes',
                  text: 'Nieuwe notitie',
                  onAction() {
                    openDrawer({ type: 'note.add' });
                  },
                },
              ]}
            >
              Toevoegen
            </ButtonMenu>
          </>
        }
      />

      <Subnav
        className="area-page-subnav"
        links={[
          linkOptions({
            to: '/claims/$guid',
            params: {
              guid,
            },
            label: 'Overzicht',
          }),
          linkOptions({
            to: '/claims/$guid/details',
            params: {
              guid,
            },
            label: 'Oorspronkelijke claim',
          }),
          linkOptions({
            to: '/claims/$guid/documents',
            params: {
              guid,
            },
            label: 'Documenten',
          }),
          linkOptions({
            to: '/claims/$guid/activities',
            params: {
              guid,
            },
            label: 'Activiteiten',
          }),
          linkOptions({
            to: '/claims/$guid/tasks',
            params: {
              guid,
            },
            label: 'Taken',
          }),
          linkOptions({
            to: '/claims/$guid/notes',
            params: {
              guid,
            },
            label: 'Notities',
          }),
        ]}
      />

      <div className="area-page-content content-grid">
        <div className="col-span-full">
          <Outlet />
        </div>
      </div>

      <RefProvider<HTMLDivElement>>
        {({ ref, trackedElement }) => (
          <div className="area-page-sidebar flex flex-col gap-y-32" ref={ref}>
            <Typography
              variant="display6"
              weight="medium"
              className="text-primaryMain"
            >
              Schadebehandelaar
            </Typography>

            <ClaimHandlerInformation
              name={data.claim.claimHandler.name}
              jobTitle={data.claim.claimHandler.jobTitle}
              email={data.claim.claimHandler.email}
              phone={data.claim.claimHandler.phone}
            />

            <CardList
              title="Recente notities"
              items={notes?.response ?? []}
              render={(note) => (
                <RecentNoteCard
                  note={note}
                  onClick={() => openDrawer({ type: 'note', data: note })}
                />
              )}
              resource="recente notities"
              action={
                <TextButton
                  size="tiny"
                  iconLeft={IconPlusCircle}
                  onPress={() => openDrawer({ type: 'note.add' })}
                >
                  Voeg notitie toe
                </TextButton>
              }
            />

            <TaskSidedrawer portalRef={trackedElement} />
            <ActivitySidedrawer portalRef={trackedElement} />
            <NoteSidedrawer portalRef={trackedElement} />
            <AddNoteSidedrawer
              portalRef={trackedElement}
              onSubmit={async (values) => {
                const files: Array<IAttachmentEntity> = await Promise.all(
                  values.explanation.files.map(async (file) => {
                    const base64 = await fileToBase64(file);

                    return {
                      filename: file.name,
                      mimetype: file.type,
                      encoded_document: base64,
                    };
                  }),
                );

                await createClaimNote({
                  category: values.type,
                  title: values.subject,
                  description: values.explanation.markdown,
                  attachments: files,
                });
              }}
            />
          </div>
        )}
      </RefProvider>
    </div>
  );
}
